import { useEffect, useState } from 'react'
import { Alert, Button, Group, Loader, Select, Stack, Table, Text, TextInput } from '@mantine/core'
import { IconLock, IconPlus } from '@tabler/icons-react'
import { DataCard } from '@/components/ui'
import type { OrgComAcesso } from '@/api/whatsappGateways'
import { useCriarGateway, useGateways, useOrganizacoesWagw } from '@/hooks/useWhatsappGateways'
import { DetalheDoGateway } from '@/components/admin/whatsappGateways/DetalheDoGateway'
import { mensagemDeErro } from '@/components/admin/whatsappGateways/mensagens'
import { RotuloDeEstado } from '@/components/admin/whatsappGateways/RotuloDeEstado'
import { VersaoDoGateway } from '@/components/admin/whatsappGateways/VersaoDoGateway'

/**
 * Administração → Gateways WhatsApp (spec 2026-09-23 §4/§5).
 *
 * O acesso é POR ORGANIZAÇÃO: a lista de organizações e o que se pode em cada
 * uma vêm de GET /v1/whatsapp-gateways/organizacoes, decidido pela mesma
 * autoridade das rotas. Botão escondido é conveniência; o backend recusa igual.
 * Cor, tipografia e alinhamento de docs/DESIGN_SYSTEM.md (§2.3, §3.1, §3.4).
 */
export function GatewaysWhatsApp() {
  const { data: orgs = [], isLoading, isError, error } = useOrganizacoesWagw()
  if (isLoading) return <Loader size="sm" />
  if (isError) {
    return <Alert color="red" variant="light">{mensagemDeErro(error)}</Alert>
  }
  if (orgs.length === 0) {
    return (
      <Alert color="gray" variant="light" icon={<IconLock size={16} />}>
        Você não tem acesso a gateways WhatsApp em nenhuma organização. A permissão é dada por
        organização, no perfil de acesso de cada uma.
      </Alert>
    )
  }
  return <Conteudo orgs={orgs} />
}

/**
 * Deep link `?org=<id>&gw=<gw>` (spec 2026-11-02 §5.2, "Gerenciar no gateway" de WhatsApi →
 * API Keys). Lido uma vez, como estado inicial; valor fora das listas é ignorado em silêncio.
 */
function pedidoDaUrl(orgs: OrgComAcesso[]): { orgId: number | null; gwId: string | null } {
  const params = new URLSearchParams(window.location.search)
  const org = orgs.find((o) => String(o.id) === params.get('org'))
  if (!org) return { orgId: null, gwId: null }
  return { orgId: org.id, gwId: params.get('gw') }
}

function Conteudo({ orgs }: { orgs: OrgComAcesso[] }) {
  const [pedido] = useState(() => pedidoDaUrl(orgs))
  // Organização só existe uma: já entra escolhida (mesmo racional de SocTeamsAdmin).
  const [orgId, setOrgId] = useState<number | null>(() => pedido.orgId ?? (orgs.length === 1 ? orgs[0].id : null))
  const [gwId, setGwId] = useState<string | null>(null)
  // O gateway do link só abre depois de a lista da organização confirmar que ele está nela.
  const [gwPedido, setGwPedido] = useState<string | null>(pedido.gwId)
  const [nome, setNome] = useState('')
  const org = orgs.find((o) => o.id === orgId) ?? null
  const gateways = useGateways(orgId)
  const criar = useCriarGateway(orgId ?? 0)
  const visiveis = (gateways.data?.items ?? []).filter((g) => g.status !== 'removido')

  // Mesma limpeza de DetalheDoGateway, do lado da lista: se o gateway selecionado
  // virar `removido` sob o polling (job terminando por conta própria, ou outra
  // sessão), ele some de `visiveis` mas continuaria aberto no detalhe.
  useEffect(() => {
    if (!gwPedido || !gateways.data) return
    if (gateways.data.items.some((g) => g.id === gwPedido && g.status !== 'removido')) setGwId(gwPedido)
    setGwPedido(null)
  }, [gateways.data, gwPedido])

  useEffect(() => {
    if (!gwId) return
    const naLista = (gateways.data?.items ?? []).find((g) => g.id === gwId)
    if (naLista?.status === 'removido') setGwId(null)
  }, [gateways.data, gwId])

  return (
    <Stack gap="lg">
      <Select
        label="Organização"
        searchable
        data={orgs.map((o) => ({ value: String(o.id), label: o.name }))}
        value={orgId == null ? null : String(orgId)}
        onChange={(v) => { setOrgId(v ? Number(v) : null); setGwId(null); setGwPedido(null) }}
      />
      {org && (
        <DataCard title="Gateways">
          <Stack gap="md">
            {gateways.isLoading ? (
              <Loader size="sm" />
            ) : gateways.isError ? (
              <Alert color="red" variant="light">{mensagemDeErro(gateways.error)}</Alert>
            ) : visiveis.length === 0 ? (
              <Text size="sm" c="dimmed">Nenhum gateway nesta organização.</Text>
            ) : (
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr><Table.Th>Nome</Table.Th><Table.Th>Estado</Table.Th><Table.Th>Versão</Table.Th><Table.Th>Id</Table.Th></Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {visiveis.map((g) => (
                    <Table.Tr key={g.id} onClick={() => setGwId(g.id)} style={{ cursor: 'pointer' }} aria-selected={g.id === gwId}>
                      <Table.Td>{g.name}</Table.Td>
                      <Table.Td><RotuloDeEstado status={g.status} /></Table.Td>
                      <Table.Td><VersaoDoGateway tag={g.image_tag} corrente={gateways.data?.versao_corrente ?? g.image_tag} /></Table.Td>
                      <Table.Td><Text ff="monospace" size="xs">{g.id}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            {org.pode.manage && (
              <Group align="flex-end">
                <TextInput label="Nome do novo gateway" value={nome} maxLength={80} onChange={(e) => setNome(e.currentTarget.value)} />
                <Button leftSection={<IconPlus size={16} />} disabled={!nome.trim()} loading={criar.isPending}
                  onClick={() => criar.mutate(nome.trim(), { onSuccess: (g) => { setNome(''); setGwId(g.id) } })}>
                  Criar gateway
                </Button>
              </Group>
            )}
          </Stack>
        </DataCard>
      )}
      {org && gwId && <DetalheDoGateway key={gwId} org={org} gwId={gwId} onRemovido={() => setGwId(null)} />}
    </Stack>
  )
}
